/**
 * Hikvision Ultra Series K8 + DeepinMind I/M + 8K Enterprise NVRs.
 *
 * Every entry below is verified against a primary Hikvision datasheet
 * (or a distributor/Hikvision-mirror PDF of the same datasheet) — sources
 * are cited inline. Numbers are copied verbatim; any field the source does
 * not state is left undefined.
 *
 * NOTES:
 *  • `NVRSpec.raidLevels` union lacks '50' / '60'. Several 96000-series +
 *    M16/R units advertise these — encoded in inline comments only.
 *  • `NVRSpec.formFactor` has no '1.5U' / '6U'. We map 1.5U → '2U' and 6U → '4U'
 *    (same convention used by hikvisionCatalog.ts).
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_NVR_ULTRA: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series K8 (older K-series, no built-in PoE, 8 SATA, 2U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-8608ni-k8',
    manufacturer: 'Hikvision',
    model: 'DS-8608NI-K8',
    category: 'nvr-recorder',
    tagline: '8-ch Ultra K8 NVR, 8 HDD bays up to 8 TB, 80 Mbps in / 160 Mbps out',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8616NI-K8.PDF',
    specs: {
      channels: 8, maxResolution: '8 MP',
      inputBandwidthMbps: 80, outputBandwidthMbps: 160,
      hddBays: 8, hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-8616ni-k8',
    manufacturer: 'Hikvision',
    model: 'DS-8616NI-K8',
    category: 'nvr-recorder',
    tagline: '16-ch Ultra K8 NVR, 8 HDD bays up to 8 TB, 160 Mbps in / 160 Mbps out',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8616NI-K8.PDF',
    specs: {
      channels: 16, maxResolution: '8 MP',
      inputBandwidthMbps: 160, outputBandwidthMbps: 160,
      hddBays: 8, hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-8632ni-k8',
    manufacturer: 'Hikvision',
    model: 'DS-8632NI-K8',
    category: 'nvr-recorder',
    tagline: '32-ch Ultra K8 NVR, 8 HDD bays up to 8 TB, 256 Mbps in / 160 Mbps out',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8616NI-K8.PDF',
    specs: {
      channels: 32, maxResolution: '8 MP',
      inputBandwidthMbps: 256, outputBandwidthMbps: 160,
      hddBays: 8, hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series I8 (8600 family, 8 SATA, 2U, RAID 0/1/5/6/10, 12 MP)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-8616ni-i8',
    manufacturer: 'Hikvision', model: 'DS-8616NI-I8',
    category: 'nvr-recorder',
    tagline: '16-ch Ultra I8 NVR, 8 HDD bays up to 10 TB, 256 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8664NI-I8.pdf',
    specs: {
      channels: 16, maxResolution: '12 MP',
      inputBandwidthMbps: 256, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-8632ni-i8',
    manufacturer: 'Hikvision', model: 'DS-8632NI-I8',
    category: 'nvr-recorder',
    tagline: '32-ch Ultra I8 NVR, 8 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8664NI-I8.pdf',
    specs: {
      channels: 32, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-8664ni-i8',
    manufacturer: 'Hikvision', model: 'DS-8664NI-I8',
    category: 'nvr-recorder',
    tagline: '64-ch Ultra I8 NVR, 8 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://hikvisiontr.net/Content/Images/uploaded/DS-8664NI-I8.pdf',
    specs: {
      channels: 64, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series I8 — 9600 family (DeepinMind-capable, 8 SATA, 2U, 12 MP)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-9616ni-i8',
    manufacturer: 'Hikvision', model: 'DS-9616NI-I8',
    category: 'nvr-recorder',
    tagline: '16-ch DeepinMind-ready NVR, 8 HDD bays up to 10 TB, 256 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.sdilink.com/Specsheet/DS-9616NI-I8-4TB.pdf',
    specs: {
      channels: 16, maxResolution: '12 MP',
      inputBandwidthMbps: 256, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9632ni-i8',
    manufacturer: 'Hikvision', model: 'DS-9632NI-I8',
    category: 'nvr-recorder',
    tagline: '32-ch DeepinMind-ready NVR, 8 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.sdilink.com/Specsheet/DS-9616NI-I8-4TB.pdf',
    specs: {
      channels: 32, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9664ni-i8',
    manufacturer: 'Hikvision', model: 'DS-9664NI-I8',
    category: 'nvr-recorder',
    tagline: '64-ch DeepinMind-ready NVR, 8 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.sdilink.com/Specsheet/DS-9616NI-I8-4TB.pdf',
    specs: {
      channels: 64, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series I16 (16-bay DeepinMind, 3U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-9616ni-i16',
    manufacturer: 'Hikvision', model: 'DS-9616NI-I16',
    category: 'nvr-recorder',
    tagline: '16-ch DeepinMind NVR, 16 HDD bays up to 10 TB, 256 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.megateh.eu/files/hikvisionv/hik9/datasheet-ds-9664ni-i16.pdf',
    specs: {
      channels: 16, maxResolution: '12 MP',
      inputBandwidthMbps: 256, outputBandwidthMbps: 256,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9632ni-i16',
    manufacturer: 'Hikvision', model: 'DS-9632NI-I16',
    category: 'nvr-recorder',
    tagline: '32-ch DeepinMind NVR, 16 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.megateh.eu/files/hikvisionv/hik9/datasheet-ds-9664ni-i16.pdf',
    specs: {
      channels: 32, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9664ni-i16',
    manufacturer: 'Hikvision', model: 'DS-9664NI-I16',
    category: 'nvr-recorder',
    tagline: '64-ch DeepinMind NVR, 16 HDD bays up to 10 TB, 320 Mbps, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.megateh.eu/files/hikvisionv/hik9/datasheet-ds-9664ni-i16.pdf',
    specs: {
      channels: 64, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 256,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out', networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // M Series 8K (8 SATA, 14 TB per bay, 2U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-9616ni-m8',
    manufacturer: 'Hikvision', model: 'DS-9616NI-M8',
    category: 'nvr-recorder',
    tagline: '16-ch M-series 8K NVR, 8 HDD bays up to 14 TB, 320 Mbps in / 400 Mbps out',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000008/OFR000006/M000000738/Data_Sheet/Datasheet-of-DS-9616NI-M8_V4.63.010_20230705.pdf',
    specs: {
      channels: 16, maxResolution: '32 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 400,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9632ni-m8',
    manufacturer: 'Hikvision', model: 'DS-9632NI-M8',
    category: 'nvr-recorder',
    tagline: '32-ch M-series 8K NVR, 8 HDD bays up to 14 TB, 320 Mbps in / 400 Mbps out',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000742/Datasheet-of-DS-9632NI-M8_V4.63.010_20230705.pdf',
    specs: {
      channels: 32, maxResolution: '32 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 400,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9664ni-m8',
    manufacturer: 'Hikvision', model: 'DS-9664NI-M8',
    category: 'nvr-recorder',
    tagline: '64-ch M-series 8K NVR, 8 HDD bays up to 14 TB, 400 Mbps bandwidth',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000008/OFR000006/M000000746/Data_Sheet/Datasheet-of-DS-9664NI-M8_V4.63.010_20230705.pdf',
    specs: {
      channels: 64, maxResolution: '32 MP',
      inputBandwidthMbps: 400, outputBandwidthMbps: 400,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9632ni-m8-r',
    manufacturer: 'Hikvision', model: 'DS-9632NI-M8/R',
    category: 'nvr-recorder',
    tagline: '32-ch 8K NVR with redundant PSU, 8 HDD bays up to 14 TB, 320 / 400 Mbps',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000008/OFR000006/M000059559/Data_Sheet/Datasheet-of-DS-9632NI-M8_R_V4.63.010_20230705.pdf',
    specs: {
      channels: 32, maxResolution: '32 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 400,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-9664ni-m8-r',
    manufacturer: 'Hikvision', model: 'DS-9664NI-M8/R',
    category: 'nvr-recorder',
    tagline: '64-ch 8K NVR with redundant PSU, 8 HDD bays up to 14 TB, 400 Mbps',
    verified: true,
    datasheetUrl: 'https://download.discomp.cz/hikvision/Datasheets/DS-9664NI-M8-R.pdf',
    specs: {
      channels: 64, maxResolution: '32 MP',
      inputBandwidthMbps: 400, outputBandwidthMbps: 400,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Enterprise 96000 I16 Series (Super NVR, 16 SATA, 3U, redundant PSU)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-96064ni-i16',
    manufacturer: 'Hikvision', model: 'DS-96064NI-I16',
    category: 'nvr-recorder',
    tagline: '64-ch Super NVR, 16 HDD bays up to 10 TB, 512 Mbps, dual PSU, 4 GbE',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000669/Datasheet-of_DS-96000NI-I16_20221118.pdf',
    specs: {
      channels: 64, maxResolution: '32 MP',
      inputBandwidthMbps: 512, outputBandwidthMbps: 512,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264', 'MJPEG'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out (32/16 with optional extension)',
      networkPorts: '4× 10/100/1000 Mbps RJ-45 (optional ext.: 4× SFP 1.25 Gbps)',
    },
  },
  {
    id: 'hikvision-ds-96128ni-i16',
    manufacturer: 'Hikvision', model: 'DS-96128NI-I16',
    category: 'nvr-recorder',
    tagline: '128-ch Super NVR, 16 HDD bays up to 10 TB, 576 Mbps in / 512 Mbps out, dual PSU',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000669/Datasheet-of_DS-96000NI-I16_20221118.pdf',
    specs: {
      channels: 128, maxResolution: '32 MP',
      inputBandwidthMbps: 576, outputBandwidthMbps: 512,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264', 'MJPEG'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out (32/16 with optional extension)',
      networkPorts: '4× 10/100/1000 Mbps RJ-45 (optional ext.: 4× SFP 1.25 Gbps)',
    },
  },
  {
    id: 'hikvision-ds-96256ni-i16',
    manufacturer: 'Hikvision', model: 'DS-96256NI-I16',
    category: 'nvr-recorder',
    tagline: '256-ch Super NVR, 16 HDD bays up to 10 TB, 768 Mbps, dual PSU, 4 GbE',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000669/Datasheet-of_DS-96000NI-I16_20221118.pdf',
    specs: {
      channels: 256, maxResolution: '32 MP',
      inputBandwidthMbps: 768, outputBandwidthMbps: 768,
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264', 'MJPEG'],
      onvif: true, formFactor: '3U',
      alarmIO: '16 in / 8 out (32/16 with optional extension)',
      networkPorts: '4× 10/100/1000 Mbps RJ-45 (optional ext.: 4× SFP 1.25 Gbps)',
    },
  },
  {
    id: 'hikvision-ds-96256ni-i16-h',
    manufacturer: 'Hikvision', model: 'DS-96256NI-I16/H',
    category: 'nvr-recorder',
    tagline: '256-ch Super NVR with built-in decoding board (6× HDMI, 48 ch)',
    verified: true,
    datasheetUrl: 'https://www.securityinformed.com/hikvision-ds-96256ni-i16-h-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 256, maxResolution: '12 MP',
      hddBays: 16, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10', 'JBOD'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '3U',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Enterprise M16/R 8K (16 SATA, 3U, redundant PSU)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-96128ni-m16-r',
    manufacturer: 'Hikvision', model: 'DS-96128NI-M16/R',
    category: 'nvr-recorder',
    tagline: '128-ch 8K Super NVR, 16 HDD bays up to 16 TB, 400 Mbps, redundant PSU',
    verified: true,
    datasheetUrl: 'https://download.discomp.cz/hikvision/datasheets/DS-96128NI-M16_R.pdf',
    specs: {
      channels: 128, maxResolution: '32 MP',
      inputBandwidthMbps: 400, outputBandwidthMbps: 400,
      hddBays: 16, hddBayMaxTb: 16,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '3U',
      networkPorts: '4× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // DeepinMind iDS-9600NXI-I8/16S(B) (AI human/vehicle, 8 SATA, 2U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ids-9616nxi-i8-16s-b',
    manufacturer: 'Hikvision', model: 'iDS-9616NXI-I8/16S(B)',
    category: 'nvr-recorder',
    tagline: '16-ch DeepinMind AI NVR, 8 HDD bays up to 10 TB, 320 Mbps in / 250 Mbps out',
    verified: true,
    datasheetUrl: 'https://www.sourcesecurity.com/datasheets/hikvision-ids-9632nxi-i8-16s-b-network-video-recorder-nvr/co-3425-ga/iDS-9600NXI-I8_16S(B).pdf',
    specs: {
      channels: 16, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 250,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out (16/8 optional)',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ids-9632nxi-i8-16s-b',
    manufacturer: 'Hikvision', model: 'iDS-9632NXI-I8/16S(B)',
    category: 'nvr-recorder',
    tagline: '32-ch DeepinMind AI NVR, 8 HDD bays up to 10 TB, 320 Mbps in / 250 Mbps out',
    verified: true,
    datasheetUrl: 'https://www.sourcesecurity.com/datasheets/hikvision-ids-9632nxi-i8-16s-b-network-video-recorder-nvr/co-3425-ga/iDS-9600NXI-I8_16S(B).pdf',
    specs: {
      channels: 32, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 250,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out (16/8 optional)',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ids-9664nxi-i8-16s-b',
    manufacturer: 'Hikvision', model: 'iDS-9664NXI-I8/16S(B)',
    category: 'nvr-recorder',
    tagline: '64-ch DeepinMind AI NVR, 8 HDD bays up to 10 TB, 320 Mbps in / 250 Mbps out',
    verified: true,
    datasheetUrl: 'https://www.sourcesecurity.com/datasheets/hikvision-ids-9632nxi-i8-16s-b-network-video-recorder-nvr/co-3425-ga/iDS-9600NXI-I8_16S(B).pdf',
    specs: {
      channels: 64, maxResolution: '12 MP',
      inputBandwidthMbps: 320, outputBandwidthMbps: 250,
      hddBays: 8, hddBayMaxTb: 10,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
      alarmIO: '16 in / 4 out (16/8 optional)',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // DeepinMind M Series 8K — iDS-96xxNXI-M8/X
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ids-9616nxi-m8-x',
    manufacturer: 'Hikvision', model: 'iDS-9616NXI-M8/X',
    category: 'nvr-recorder',
    tagline: '16-ch DeepinMind 8K AI NVR, 8 HDD bays up to 14 TB',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000023/OFR000031/M000065339/Data_Sheet/Datasheet-of-iDS-9616NXI-M8_X-DeepinMind-NVR_V4.61.200_20230524.pdf',
    specs: {
      channels: 16, maxResolution: '32 MP',
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
    },
  },
  {
    id: 'hikvision-ids-9632nxi-m8-x',
    manufacturer: 'Hikvision', model: 'iDS-9632NXI-M8/X',
    category: 'nvr-recorder',
    tagline: '32-ch DeepinMind 8K AI NVR, 8 HDD bays up to 14 TB, 320 Mbps',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000018319/S000000023/OFR031268/M000065341/Data_Sheet/Datasheet-of-iDS-9632NXI-M8_X-DeepinMind-NVR_V4.61.200_20230524.pdf',
    specs: {
      channels: 32, maxResolution: '32 MP',
      inputBandwidthMbps: 320,
      hddBays: 8, hddBayMaxTb: 14,
      raidLevels: ['0', '1', '5', '6', '10'],
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U',
    },
  },
];
